import { BearingHubPageStoreProvider } from '@hub/hooks/states/useBearingHubPageStore';
import { BearingHubHelper, SortingState } from '@hub/libs/bearing-hub.helper';
import { getNodeContactProp } from '@hub/libs/node-contact.helper';
import { getLocale } from '@hub/libs/constants';
import type { BearingHubPageParams } from '@hub/models/data';
import type { BearingHubPageProps, ExtendedBearingHubPageProps } from '@hub/bearing-hub/BearingHubPage/BearingHubPage';
import { BearingHubPage } from '@hub/bearing-hub/BearingHubPage/BearingHubPage';
import { getPublicShopSession } from '@hub/libs/catalog-service';
import { IbhFilterValuesStoreProvider } from '@hub/hooks/states/useIbhFilterValuesStore';
import { getIbhLanguageAlternates, getServerSideTranslations } from '@hub/libs/i18n.helper';
import { BearingsTableStoreProvider } from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { Session } from '@hub/hooks/states/useSessionStore';
import { BEARINGHUB_NODEID_OVERWRITES, DEFAULT_QUANTITY } from '@hub/bearing-hub/common/constants';
import { getInitialLayoutData } from '@hub/page-helpers/getInitialLayoutData';
import { fetchProductionMethods } from '@hub/bearing-hub/common/productionMethods/fetchProductionMethods';
import { fetchMaterials } from '@hub/bearing-hub/common/materials/fetchMaterials';
import { fetchParts } from '@hub/bearing-hub/BearingHubPage/parts/fetchParts';
import { fetchPartsLifetime } from '@hub/bearing-hub/BearingHubPage/parts/fetchPartsLifetime';
import type { NextRuntimeConfiguration } from '@hub/libs/config';
import getConfig from 'next/config';
import { getSessionPayload } from '@hub/common/Session/session.utils';
import {
  createLifetimeParametersStore,
  LifetimeParametersStoreProvider,
} from '@hub/bearing-hub/libs/BearingsLifetime/useIbhLifetimeParametersValuesStore';
import { Logger } from '@icommerce/logger';
import { fetchShapes } from '@hub/bearing-hub/common/shapes/fetchShapes';
import { UnitSystem } from '@igus/kopla-domain';
import { compareBearingTableValues } from '@hub/bearing-hub/BearingHubPage/BearingsTable/bearingsTableSorting.helper';

const { serverRuntimeConfig } = getConfig() as NextRuntimeConfiguration;

export const getStaticPaths: GetStaticPaths = () => ({
  paths: [],
  fallback: 'blocking',
});

export const getStaticProps: GetStaticProps<BearingHubPageProps, BearingHubPageParams> = async context => {
  const locale = getLocale(context);
  const unitSystem = UnitSystem.metric;
  const shapePath = context.params?.shape;

  if (!shapePath) {
    Logger.error({ message: `Missing shape parameter like .../iglid(ur|e)-ibh/[sleeve-bearings]` });
    return { notFound: true, revalidate: serverRuntimeConfig.ibhStaticSiteCacheTtl };
  }

  const shapes = await fetchShapes();
  const availableShapes = BearingHubHelper.getShapeDescriptors(locale, shapes);
  let selectedShape = BearingHubHelper.getShapeByShapePath(shapePath, locale, shapes);

  // try to find default shapes before we give up and return a four-oh-four
  if (!selectedShape) {
    selectedShape = BearingHubHelper.getShapeByShapePath(shapePath, context.defaultLocale || 'en', shapes);
  }

  if (!selectedShape) {
    Logger.error({ message: `Shape not found with parameter shapePath = "${shapePath}"` });
    return { notFound: true, revalidate: serverRuntimeConfig.ibhStaticSiteCacheTtl };
  }

  const session: Session = await getPublicShopSession(getSessionPayload(locale));

  const productionMethods = await fetchProductionMethods(selectedShape.shapeKey, session);

  const materials = await fetchMaterials({
    shape: selectedShape.shapeKey,
    countryCode: session.country,
  });

  const initialPartsRequest = {
    shape: selectedShape.shapeKey,
    quantity: DEFAULT_QUANTITY,
    materials: materials.map(material => material.id),
    unitSystem,
  };
  const initialLifetimeConfig = createLifetimeParametersStore(unitSystem).getState().getLifetimeConfigPayload();
  const [bearingPartsData, initialBearingPartsLifetime] = await Promise.all([
    fetchParts({ sid: session.sessionId }, initialPartsRequest),
    fetchPartsLifetime(
      { sid: session.sessionId },
      { ...initialPartsRequest, lifetimeConfig: initialLifetimeConfig }
    ).catch((error: unknown): undefined => {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error({ message: `Unable to load initial bearing parts lifetime: ${message}` });
      return undefined;
    }),
  ]);

  const bearingParts = bearingPartsData.sort((a, b) =>
    compareBearingTableValues(a.unitPrice.value, b.unitPrice.value, SortingState.Ascending)
  );

  // check for overwrites, else extract node id from productsResult
  const nodeContactId = BEARINGHUB_NODEID_OVERWRITES.IBH;

  const contact = await getNodeContactProp(nodeContactId, locale);

  const languageAlternates = getIbhLanguageAlternates(locale, { selectedShape });
  const layoutData = await getInitialLayoutData(locale, languageAlternates);

  // TODO: Adjust this to use modern FooterProps
  // (layoutData?.footer as Partial<FooterProps>)?.subscribe?.links?.forEach(replaceLinks);
  // (layoutData?.footer as Partial<FooterProps>)?.awards?.links?.forEach((link: ImageLink) => replaceLinks(link));

  const props: ExtendedBearingHubPageProps = {
    layout: layoutData,
    languageAlternates,
    selectedShape,
    availableShapes,
    contact,
    materials,
    productionMethods,
    bearingParts,
    initialBearingPartsLifetime,
    initialUnitSystem: unitSystem,
    ...(await getServerSideTranslations(serverSideTranslations, locale, [
      'bearing-hub/bearingHub',
      'layout/marketingBanner',
      'layout/localeSelection',
      'layout/contactPersonBox',
      'layout/breadcrumb',
      'layout/header',
      'product/buyBox',
      'product/productDetails',
      'category/productFilter',
      'category/categoryPage',
      'free-form-hub/freeFormHubCommon',
      'toast/toast',
    ])),
  };

  return {
    props,
    revalidate: serverRuntimeConfig.ibhStaticSiteCacheTtl,
  };
};

const BearingHubOverviewPage = ({
  layout,
  languageAlternates,
  selectedShape,
  availableShapes,
  contact,
  materials,
  productionMethods,
  bearingParts,
  initialBearingPartsLifetime,
  initialUnitSystem,
}: ExtendedBearingHubPageProps) => (
  <BearingHubPageStoreProvider initialData={{ contact }}>
    <IbhFilterValuesStoreProvider
      materials={materials}
      productionMethods={productionMethods}
      selectedShape={selectedShape}
    >
      <BearingsTableStoreProvider initialData={{ selectedShape }}>
        <LifetimeParametersStoreProvider unitSystem={initialUnitSystem}>
          <BearingHubPage
            selectedShape={selectedShape}
            availableShapes={availableShapes}
            layout={layout}
            materials={materials}
            productionMethods={productionMethods}
            bearingParts={bearingParts}
            initialBearingPartsLifetime={initialBearingPartsLifetime}
            languageAlternates={languageAlternates}
            initialUnitSystem={initialUnitSystem}
          />
        </LifetimeParametersStoreProvider>
      </BearingsTableStoreProvider>
    </IbhFilterValuesStoreProvider>
  </BearingHubPageStoreProvider>
);

export default BearingHubOverviewPage;
